import React from 'react';
import { Seo, breadcrumbJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { SITE } from '@/data/site-config';

export function PrivacyPage() {
  const path = '/privacy-policy';
  return (
    <>
      <Seo title="Privacy Policy" description="Privacy policy for Fitness Training 360." canonical={path} jsonLd={breadcrumbJsonLd([{name:'Home',path:'/'},{name:'Privacy',path}])} />
      <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Privacy Policy', path }]} />
      <article className="container-x py-10 max-w-3xl prose-dark">
        <h1 className="font-display text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-[color:var(--ft-dim)] text-sm">Last updated: {new Date().toLocaleDateString()}. This is a plain-language summary of how Fitness Training 360 handles information collected through fit360life.com. This document is intended for professional legal review before publication.</p>
        <h2>Information we collect</h2>
        <p>When you submit a form on this site (e.g., a consultation request or contact form), we collect the information you provide — name, email, phone, and any message you send. We also receive automatic information from your browser (such as the page you submitted from and basic device information).</p>
        <h2>How we use information</h2>
        <ul>
          <li>To contact you about your inquiry.</li>
          <li>To deliver coaching services if you become a client.</li>
          <li>To improve our website and our services.</li>
        </ul>
        <h2>Email and SMS communication</h2>
        <p>If you provide a phone number, we may contact you by call or SMS about your inquiry. Standard message and data rates may apply. You can opt out at any time by replying STOP to any SMS message or by emailing {SITE.email}.</p>
        <h2>Analytics & cookies</h2>
        <p>We may use analytics tools to understand how visitors use the site. We do not send your name, phone number, email, or the contents of your form submission to analytics tools.</p>
        <h2>Third-party processors</h2>
        <p>We use third-party providers to send transactional emails (e.g., Gmail) and host this website. These providers process limited data on our behalf.</p>
        <h2>Data retention</h2>
        <p>We retain lead and client information for as long as needed to provide our services and meet our legal obligations. You can request deletion of your data by emailing {SITE.email}.</p>
        <h2>Children</h2>
        <p>This website is not directed at children under 13 and we do not knowingly collect personal information from children.</p>
        <h2>Contact</h2>
        <p>Privacy inquiries: <a className="text-accent" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
      </article>
    </>
  );
}

export function TermsPage() {
  const path = '/terms';
  return (
    <>
      <Seo title="Terms of Service" description="Terms of service for Fitness Training 360." canonical={path} jsonLd={breadcrumbJsonLd([{name:'Home',path:'/'},{name:'Terms',path}])} />
      <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Terms', path }]} />
      <article className="container-x py-10 max-w-3xl prose-dark">
        <h1 className="font-display text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-[color:var(--ft-dim)] text-sm">Last updated: {new Date().toLocaleDateString()}. Intended for professional legal review before final publication.</p>
        <h2>Use of the website</h2>
        <p>By using fit360life.com you agree to these terms. The site is provided as-is. We reserve the right to update content at any time.</p>
        <h2>No medical advice</h2>
        <p>The information on this website and during our coaching is general fitness guidance. It is not medical advice. Please consult your physician before starting any exercise or nutrition program.</p>
        <h2>Limitation of liability</h2>
        <p>To the maximum extent permitted by law, Fitness Training 360 is not liable for indirect or consequential damages arising from use of this website.</p>
        <h2>Governing law</h2>
        <p>These terms are governed by the laws of the State of Florida.</p>
        <h2>Contact</h2>
        <p>Questions: <a className="text-accent" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
      </article>
    </>
  );
}

export function AccessibilityPage() {
  const path = '/accessibility';
  return (
    <>
      <Seo title="Accessibility" description="Our accessibility statement." canonical={path} jsonLd={breadcrumbJsonLd([{name:'Home',path:'/'},{name:'Accessibility',path}])} />
      <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Accessibility', path }]} />
      <article className="container-x py-10 max-w-3xl prose-dark">
        <h1 className="font-display text-4xl font-bold mb-4">Accessibility</h1>
        <p>We aim to make fit360life.com accessible to as many people as possible. We design with semantic HTML, visible keyboard focus, color contrast that meets common standards, and we honor your reduced-motion preferences.</p>
        <h2>Reporting issues</h2>
        <p>If you encounter an accessibility barrier on the site, please email <a className="text-accent" href={`mailto:${SITE.email}`}>{SITE.email}</a> with the page URL and a brief description, and we’ll address it as soon as we can.</p>
      </article>
    </>
  );
}
